import { TEXTOS } from '../../../lib/glossario'
import type { ConsumoDoMes, PrecoDoToken, SaldoDaIa } from '../../../types/creditoDaIa'
import { comProcedencia, emMoeda, emUnidades } from './dinheiro'

interface Props {
  saldo: SaldoDaIa | null
  consumo: ConsumoDoMes
  /**
   * O preço em vigor.
   *
   * Serve a duas coisas: confrontar o modelo declarado com os que de fato rodaram, e — quando o mês
   * ainda não consumiu nada — dar procedência ao "R$ 0,00 de R$ 300,00". Um valor em reais sem o
   * preço ao lado é exatamente o print que a V31 recusa, e o mês vazio é o caso em que ele mais
   * facilmente escaparia.
   */
  precoVigente: PrecoDoToken | null
}

/**
 * O saldo do mês e o consumo — com o medido separado do presumido, sempre.
 *
 * **Nenhum valor aparece sem o preço ao lado.** É a quinta camada de defesa da leitura que a V31
 * recusou: um número acompanhado de "Microsoft Azure", do preço por milhão e do nome de quem o
 * declarou não se confunde com medição do contrato com a contratada.
 *
 * **E o total nunca aparece como um número só.** Hoje 100% do consumo é presunção — nenhuma das
 * execuções trouxe medição de uso —, e mostrar um total com cara de medido seria a mentira exata que
 * a V31 proibiu, agora em reais.
 *
 * @param props o saldo, o consumo, o modelo do preço e se há preço declarado
 * @returns o cartão do saldo
 */
export function CartaoDoSaldo({ saldo, consumo, precoVigente }: Props) {
  const t = TEXTOS.creditoDaIa.saldo
  const modeloDeReferencia = precoVigente?.modeloDeReferencia ?? null
  const divergentes = consumo.modelos.filter((m) => modeloDeReferencia && m !== modeloDeReferencia)

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="text-base font-semibold text-slate-900">{t.titulo}</h2>

      {!saldo && (
        <p className="mt-2 max-w-prose text-sm text-slate-700">
          {t.semCredito}{' '}
          <span className="whitespace-nowrap font-medium text-slate-900">
            {emUnidades(consumo.tokens)} unidades de uso neste mês
          </span>
          .
        </p>
      )}

      {saldo && <Numeros saldo={saldo} consumo={consumo} precoVigente={precoVigente} />}

      {/* A ressalva do "tudo presumido" mora AQUI, junto dos outros avisos, e não dentro de
          `<Numeros>`. Dentro dela só renderizava quando havia crédito declarado — ou seja, ela
          nunca aparecia exatamente no estado em que TODO o consumo é presunção, que é o estado da
          produção hoje: 6 pareceres, 6 sem medição, 0 créditos, 0 preços. O administrador lia
          "36.000 unidades de uso neste mês" e dimensionava o primeiro crédito em reais por um
          número que é 6 × o custo presumido, sem uma medição sequer por trás. */}
      {consumo.tudoPresumido && consumo.execucoes > 0 && (
        <Aviso tom="ambar" texto={t.tudoPresumido(consumo.execucoes)} />
      )}

      {saldo?.esgotado && <Aviso tom="vermelho" texto={t.esgotado} extra={t.oQueContinua} />}
      {saldo && !saldo.esgotado && saldo.naReserva && <Aviso tom="ambar" texto={t.naReserva} />}

      {consumo.tokensSemPreco > 0 && <Aviso tom="ambar" texto={t.semPreco(emUnidades(consumo.tokensSemPreco))} />}
      {divergentes.length > 0 && modeloDeReferencia && (
        <Aviso tom="ambar" texto={t.modeloDivergente(modeloDeReferencia, divergentes.join(', '))} />
      )}

      <p className="mt-4 max-w-prose border-t border-slate-100 pt-3 text-xs text-slate-500">
        {TEXTOS.creditoDaIa.naoEhContrato}
      </p>
    </section>
  )
}

/** Os números do saldo — e o medido nunca some dentro do presumido. */
function Numeros({
  saldo,
  consumo,
  precoVigente,
}: {
  saldo: SaldoDaIa
  consumo: ConsumoDoMes
  precoVigente: PrecoDoToken | null
}) {
  const t = TEXTOS.creditoDaIa.saldo
  // O preço que de fato governou o mês; sem consumo nenhum, o que vale é o preço em vigor — porque
  // o "R$ 0,00 de R$ 300,00" acima já é um valor em reais, e ele não pode aparecer sozinho.
  const preco = consumo.porPreco[0]?.preco ?? precoVigente
  const total = preco ? comProcedencia(consumo.total, preco) : null

  return (
    <>
      <p className="mt-2 text-2xl font-semibold tabular-nums text-slate-900">
        {t.consumidoDe(emMoeda(consumo.total), emMoeda(saldo.credito.valor))}
      </p>
      <p className="mt-0.5 text-sm text-slate-700">{t.restante(emMoeda(saldo.restante))}</p>

      <Barra fracao={saldo.fracaoConsumida} esgotado={saldo.esgotado} naReserva={saldo.naReserva} />

      <p className="mt-2 text-sm text-slate-700">
        {t.medidoEPresumido(emMoeda(consumo.medido), emMoeda(consumo.presumido), consumo.semMedicao)}
      </p>

      {consumo.emMoedaDeCobranca && consumo.moedaDeCobranca && (
        <p className="mt-1 text-xs text-slate-500">
          {t.emDolar(emMoeda(consumo.emMoedaDeCobranca, consumo.moedaDeCobranca))}
        </p>
      )}

      {total && <p className="mt-2 max-w-prose text-xs text-slate-500">{total.procedencia}</p>}
      {!preco && <p className="mt-2 text-xs text-slate-500">{TEXTOS.creditoDaIa.preco.semPrecos}</p>}
    </>
  )
}

/** A barra do consumo. Cor sozinha não é sinal: os textos acima dizem a mesma coisa. */
function Barra({ fracao, esgotado, naReserva }: { fracao: number; esgotado: boolean; naReserva: boolean }) {
  const cor = esgotado ? 'bg-red-600' : naReserva ? 'bg-amber-500' : 'bg-niteroi-600'
  return (
    <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-200" aria-hidden="true">
      <div className={`h-full ${cor}`} style={{ width: `${Math.min(Math.max(fracao, 0), 1) * 100}%` }} />
    </div>
  )
}

/** Tarja de aviso. Não é `role="alert"`: nada aqui é falha, e alerta que grita se aprende a ignorar. */
function Aviso({ tom, texto, extra }: { tom: 'ambar' | 'vermelho'; texto: string; extra?: string }) {
  const cores =
    tom === 'vermelho' ? 'border-red-200 bg-red-50 text-red-900' : 'border-amber-200 bg-amber-50 text-amber-900'
  return (
    <div className={`mt-3 max-w-prose rounded-lg border px-3 py-2 text-sm ${cores}`}>
      <p>{texto}</p>
      {extra && <p className="mt-1.5 text-xs">{extra}</p>}
    </div>
  )
}
